import React, { useCallback, useEffect } from 'react';
import {Box, FormControl, InputLabel, Link, MenuItem, Typography} from '@material-ui/core';
import i18nNamespaces from '../../../const/i18nNamespaces';
import {Trans, useTranslation} from 'react-i18next';
import {AppointmentDetailsForPrescriptionDto} from '../../../types/appointments';
import {CustomButton, CustomSelect, Loading} from '../../index';
import {useAppDispatch, useAppSelector} from '../../../hooks/customReduxHooks';
import {
    fetchEwusNfzRegionalBranches,
	fetchEwusVerification,
	selectEwusNfzRegionalBranches,
	selectEwusPatientInsuranceDeclaration,
	selectEwusPatientNfzRegionalBranchCode,
	selectEwusResult,
	selectEwusStatus,
	setEwusPatientNfzRegionalBranch,
    setEwusPatientNfzRegionalBranchCode,
} from '../../../store/ewusSlice';
import { RegionalBranch } from '../../../types/insuranceDeclaration';
import { EwusVerificationResultMessage } from '../../../types/ewus';
import { Info, Search } from '@material-ui/icons';
import Pill from '../../common/Pill/Pill';
import ErrorIcon from '../../common/icons/ErrorIcon/ErrorIcon';
import WarningIcon from '../../common/icons/WarningIcon/WarningIcon';
import InfoIcon from '../../common/icons/InfoIcon/InfoIcon';
import { ewusApi } from '../../../api/ewus/ewusApi';
import { MEDIC_PROFILE_PAGE, MEDIC_SETTINGS } from '../../../const/routes';
import useTabFocusEffect from '../../../hooks/useTabFocusEffect';

interface EwusTabProps {
	appointmentDetails: AppointmentDetailsForPrescriptionDto;
}

interface EwusMessagesProps {
	title: string;
	messages: EwusVerificationResultMessage[];
}

const EwusMessages = ({title, messages}: EwusMessagesProps) => {
	const getIconByResult = (result: string) => {
		switch (result) {
			case 'B': return <ErrorIcon />;
			case 'O': return <WarningIcon />;
			default: return <InfoIcon />;
		}
	};
	return (
		<>
			<h2>{title}</h2>
			{messages.map((message, index) => (
				<Box key={`${message.code}-${index}`} display="flex" alignItems="flex-start" mb={1}>
					{getIconByResult(message.result)}
					<Typography style={{marginLeft: 8}}>{message.description}</Typography>
				</Box>
			))}
		</>
	);
}

const EwusTab = ({appointmentDetails}: EwusTabProps) => {
	const {t} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
	const dispatch = useAppDispatch();
    const nfzPatientRegionalBranchCode = useAppSelector(selectEwusPatientNfzRegionalBranchCode);
	const patientDeclaredPublicInsurance = useAppSelector(selectEwusPatientInsuranceDeclaration);
	const regionalBranchList = useAppSelector(selectEwusNfzRegionalBranches);
	const status = useAppSelector(selectEwusStatus);
	const ewusResult = useAppSelector(selectEwusResult);
	const [areCredentialsFilled, setAreCredentialsFilled] = React.useState<boolean>(false);
	const loading = status === 'loading';
	const patientPesel =
		appointmentDetails?.child?.pesel || appointmentDetails?.patient?.pesel;
	const ewusCredentialsInMedicProfileUrl = `${MEDIC_SETTINGS}${MEDIC_PROFILE_PAGE}#ewusSecretForm`;

	const isVerifyDisabled = loading || !patientPesel || !areCredentialsFilled;
	const shouldShowVerifyButton = areCredentialsFilled && (!ewusResult || !ewusResult?.status);
	const shouldShowEwusResult = areCredentialsFilled && !!ewusResult && ewusResult.status !== null;
	const shouldShowEwusMessages = areCredentialsFilled && (ewusResult?.ewusVerificationResults?.length ?? 0) > 0;

	const fetchEwusCredentials = useCallback(async () => {
		try {
			const {data} = await ewusApi.getEwusMedicCredentials();
			setAreCredentialsFilled(data.isSuccess);
		} catch (error) {
			console.error(error);
		}
	}, []);

	useEffect(() => {
		fetchEwusCredentials();
	}, [fetchEwusCredentials]);

	useTabFocusEffect(fetchEwusCredentials);

	useEffect(() => {
		if (!regionalBranchList.length) {
			dispatch(fetchEwusNfzRegionalBranches());
		}
	}, [dispatch, regionalBranchList.length]);

	useEffect(() => {
		const selectedBranch = regionalBranchList.find(branch => branch.code === nfzPatientRegionalBranchCode) ?? null;
		dispatch(setEwusPatientNfzRegionalBranch(selectedBranch));
	}, [dispatch, nfzPatientRegionalBranchCode, regionalBranchList]);

	const handleVerify = async () => {
		if (!patientPesel) return;
		dispatch(
			fetchEwusVerification({
				appointmentId: appointmentDetails.id,
				patientPesel,
			}),
		);
	};

	const handleChangeRegionalBranch = (event: React.ChangeEvent<{ value: unknown }>) => {
		dispatch(
			setEwusPatientNfzRegionalBranchCode({
				patientId: appointmentDetails.patient.userId,
				nfzPatientRegionalBranchCode: event.target.value as RegionalBranch['code'],
			}),
		);
	}

	if (!areCredentialsFilled) {
		return (
			<>
				<Loading loading={loading} withBackdrop />
				<Box style={{marginTop: 30, marginBottom: 30}}>
					<Box display="flex" alignItems="center">
						<Info fontSize="small" color="disabled" />
						<Typography style={{marginLeft: 8}}>
							<Trans
								t={t}
								i18nKey={'ewusCredentialsNotLoaded'}
								components={[
									<Link
										color="secondary"
										key={'0'}
										href={ewusCredentialsInMedicProfileUrl}
										target="_blank"
										rel="noopener noreferrer"
									/>,
								]}
							/>
						</Typography>
					</Box>
				</Box>
			</>
		);
	}
    
	return (
		<>
			<Loading loading={loading} withBackdrop />
			{shouldShowVerifyButton && (
				<Box style={{marginTop: 30, marginBottom: 30}}>
					<CustomButton
						variant={'contained'}
						color={'secondary'}
						onClick={handleVerify}
						disabled={isVerifyDisabled}
						startIcon={<Search />}
					>
						{t('checkInEwus')}
					</CustomButton>
				</Box>
			)}
			{shouldShowEwusResult && (
				<>
					<h2>{t('dataFromEwusSystem')}</h2>
					<Pill 
						content={
							ewusResult.status ? t('patientInsured') : t('patientNotInsured')
						} 
						color={ewusResult.status ? 'success' : 'default'} 
						style={{marginBottom: 16}} 
					/>
					{ewusResult.status === true && (
						<>
							<Typography style={{marginBottom: 16, fontWeight: 'bold'}}>
								{`${ewusResult.name} ${ewusResult.surname}`}
							</Typography>
							<Typography style={{marginBottom: 16}}>
								{!patientDeclaredPublicInsurance && t('patientDidNotApplyForReimbursementOrDeclaredNotInsured')}
							</Typography>
							<FormControl variant="outlined" fullWidth style={{marginBottom: 24}}>
								<InputLabel id="nfzBranchLabel">
									{t('declaredNfzBranch')}
								</InputLabel>
								<CustomSelect
									labelId={'nfzBranchLabel'}
									value={nfzPatientRegionalBranchCode ?? ''}
									label={t('declaredNfzBranch')}
									onChange={handleChangeRegionalBranch}
									variant='outlined'
									fullWidth
								>
									{regionalBranchList.map(
										regionalBranchItem => (
											<MenuItem
												value={regionalBranchItem.code}
												key={regionalBranchItem.code}
											>
												{regionalBranchItem.name}
											</MenuItem>
										),
									)}
								</CustomSelect>
							</FormControl>
						</>
					)}
				</>
			)}
			{shouldShowEwusMessages && (
				<EwusMessages
					title={t('ewusMessages')}
					messages={ewusResult.ewusVerificationResults}
				/>
			)}
			
		</>
	);
};

export default EwusTab;
